import { useEffect, useState } from 'react'
import { getApiUrl } from '@/lib/env'
import { getSession } from '@/lib/auth-client'
import { getAuthHeaders, queryClient } from '@/lib/trpc'
import { isStorageImageUrl, resolveStorageImageUrl } from '@/utils/storage-url'

type AuthenticatedImageState = {
  sourceUrl: string
  workspaceId: string | null
  imageUrl: string | null
  status: 'loading' | 'ready' | 'error'
}

export function useAuthenticatedImageUrl(
  sourceUrl: string | null | undefined
): {
  imageUrl: string | null
  isLoading: boolean
  isError: boolean
} {
  const [state, setState] = useState<AuthenticatedImageState | null>(null)
  const requiresAuthentication = isStorageImageUrl(sourceUrl)
  const workspaceId = localStorage.getItem('auth:activeWorkspaceId')

  useEffect(() => {
    if (!sourceUrl || !isStorageImageUrl(sourceUrl)) return

    let cancelled = false
    let objectUrl: string | null = null

    setState({ sourceUrl, workspaceId, imageUrl: null, status: 'loading' })

    void (async () => {
      try {
        const storageUrl = resolveStorageImageUrl(sourceUrl)
        const separator = storageUrl.includes('?') ? '&' : '?'
        const session = await getSession()
        // Cache blobs, not object URLs: each mounted consumer owns its URL's lifetime.
        // The shared auth query cache is cancelled and cleared on sign-out.
        const blob = await queryClient.fetchQuery({
          queryKey: ['authenticated-image', storageUrl, {
            workspaceId,
            sessionId: session?.session.id,
            organizationId: session?.session.activeOrganizationId,
          }],
          staleTime: 30_000,
          gcTime: 30_000,
          retry: false,
          meta: { skipGlobalErrorHandler: true },
          queryFn: async ({ signal }) => {
            const response = await fetch(getApiUrl(`${storageUrl}${separator}inline=1`), {
              credentials: 'include',
              headers: await getAuthHeaders(),
              signal,
            })
            if (!response.ok) {
              throw new Error(`Failed to load authenticated image: ${response.status}`)
            }
            return response.blob()
          },
        })
        const nextObjectUrl = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl)
          return
        }
        objectUrl = nextObjectUrl
        setState({ sourceUrl, workspaceId, imageUrl: objectUrl, status: 'ready' })
      } catch {
        if (!cancelled) {
          setState({ sourceUrl, workspaceId, imageUrl: null, status: 'error' })
        }
      }
    })()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [sourceUrl, workspaceId])

  if (!sourceUrl) {
    return { imageUrl: null, isLoading: false, isError: false }
  }
  if (!requiresAuthentication) {
    return { imageUrl: sourceUrl, isLoading: false, isError: false }
  }
  if (!state || state.sourceUrl !== sourceUrl || state.workspaceId !== workspaceId) {
    return { imageUrl: null, isLoading: true, isError: false }
  }

  return {
    imageUrl: state.imageUrl,
    isLoading: state.status === 'loading',
    isError: state.status === 'error',
  }
}
